import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { ensureXiaolaiFont } from "../utils/ensureXiaolaiFont";
import { useAppI18n } from "../hooks/useAppI18n";

import { VoiceInputButton } from "./VoiceInputButton";
import {
  AttachImageThumbnails,
  readFilesAsDataUrls,
} from "./AttachImage";
import {
  AttachFileThumbnails,
  readFilesAsBase64,
  type AttachedFile,
} from "./AttachFile";
import { AttachButton } from "./AttachButton";
import { UploadToast } from "./UploadToast";
import { ConversationContent } from "./ConversationContent";
import { ModeToggle, type AppMode } from "./ModeToggle";
import { useFileSizeToast } from "../hooks/useFileSizeToast";

import "./EntryScreen.scss";

import type { DiagramListItem } from "../data/diagramStorage";

// ─── Props ───────────────────────────────────────────────────────────────────

interface EntryScreenProps {
  userName?: string;
  userAvatarUrl?: string | null;
  userInitials?: string;
  onUserClick?: () => void;
  onWeaveIdea: (
    prompt: string,
    initialImageUrls?: string[],
    initialFiles?: AttachedFile[],
  ) => void;
  onDrawDirect: (
    prompt: string,
    initialImageUrls?: string[],
    initialFiles?: AttachedFile[],
  ) => void;
  onStartCanvas: () => void;
  recentDiagrams?: DiagramListItem[];
  onSelectProject?: (id: string) => void;
  isLoadingRecent?: boolean;
  isAuthenticated?: boolean;
  isAuthLoading?: boolean;
  isDark?: boolean;
  // Conversation expansion
  isConversationActive?: boolean;
  conversationTopic?: string;
  conversationInitialImageUrls?: string[];
  conversationInitialFiles?: AttachedFile[];
  onBackFromConversation?: () => void;
  onGoToDraw?: (
    context: import("./ConversationContent").ConversationContextForDraw,
  ) => void;
  userId?: string | null;
  onOpenAuthModal?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const EntryScreen: React.FC<EntryScreenProps> = ({
  userName = "there",
  userAvatarUrl,
  userInitials,
  onUserClick,
  onWeaveIdea,
  onDrawDirect,
  onStartCanvas: _onStartCanvas,
  recentDiagrams = [],
  onSelectProject,
  isLoadingRecent = false,
  isAuthenticated = false,
  isAuthLoading = false,
  isDark = false,
  isConversationActive = false,
  conversationTopic,
  conversationInitialImageUrls,
  conversationInitialFiles,
  onBackFromConversation,
  onGoToDraw,
  userId,
  onOpenAuthModal,
}) => {
  const { t, langCode } = useAppI18n();
  const isChinese = langCode.startsWith("zh");
  const { toastMessage, showFileTooLarge, dismissToast } = useFileSizeToast();

  // ── App mode (Draw / Weave) ────────────────────────────────────────────────
  const [appMode, setAppMode] = useState<AppMode>("draw");
  const [cardPulse, setCardPulse] = useState(false);

  const handleModeChange = useCallback((newMode: AppMode) => {
    setAppMode(newMode);
    setCardPulse(true);
  }, []);

  // ── Conversation quota modal ────────────────────────────────────────────────
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaIsAnonymous, setQuotaIsAnonymous] = useState(false);

  const handleConversationQuotaExceeded = useCallback(
    (info: { isAnonymous: boolean }) => {
      setQuotaIsAnonymous(info.isAnonymous);
      setShowQuotaModal(true);
    },
    [],
  );

  // ── Avatar retry logic ─────────────────────────────────────────────────────
  const [avatarImgFailed, setAvatarImgFailed] = useState(false);
  const [avatarRetrying, setAvatarRetrying] = useState(false);
  const avatarRetriesRef = useRef(0);
  const MAX_AVATAR_RETRIES = 2;

  useEffect(() => {
    setAvatarImgFailed(false);
    setAvatarRetrying(false);
    avatarRetriesRef.current = 0;
  }, [userAvatarUrl]);

  const handleAvatarError = useCallback(() => {
    if (avatarRetriesRef.current < MAX_AVATAR_RETRIES) {
      avatarRetriesRef.current += 1;
      setAvatarRetrying(true);
      const delay = 1500 * avatarRetriesRef.current;
      setTimeout(() => setAvatarRetrying(false), delay);
    } else {
      setAvatarImgFailed(true);
    }
  }, []);

  useEffect(() => {
    ensureXiaolaiFont();
  }, []);

  // ── Entry input state ──────────────────────────────────────────────────────
  const [prompt, setPrompt] = useState("");
  const [pendingImageUrls, setPendingImageUrls] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<AttachedFile[]>([]);
  const [compositionText, setCompositionText] = useState("");
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const skipNextEnterRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const hasContent =
    Boolean(prompt.trim()) ||
    Boolean(compositionText) ||
    pendingFiles.length > 0;
  const showMicHint = !hasContent && !isTextareaFocused && !isVoiceRecording;

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) {
      return;
    }
    ta.style.height = "auto";
    const capped = Math.min(ta.scrollHeight, 200);
    ta.style.height = `${capped}px`;
  }, [prompt]);

  const handleSubmit = () => {
    if (prompt.trim() || pendingFiles.length > 0) {
      const trimmedPrompt = prompt.trim() || " ";
      const imgs = pendingImageUrls.length > 0 ? pendingImageUrls : undefined;
      const files = pendingFiles.length > 0 ? pendingFiles : undefined;

      if (appMode === "draw") {
        onDrawDirect(trimmedPrompt, imgs, files);
      } else {
        onWeaveIdea(trimmedPrompt, imgs, files);
      }
      setPendingImageUrls([]);
      setPendingFiles([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (e.nativeEvent.isComposing || skipNextEnterRef.current) {
        skipNextEnterRef.current = false;
        return;
      }
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleCompositionEnd = () => {
    setCompositionText("");
    skipNextEnterRef.current = true;
    setTimeout(() => {
      skipNextEnterRef.current = false;
    }, 100);
  };

  const handleVoiceTranscript = useCallback((text: string) => {
    setPrompt((prev) => (prev ? `${prev} ${text}` : text));
  }, []);

  // ── Drag-and-drop image upload ──────────────────────────────────────────────
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    const fileList = e.dataTransfer.files;
    if (!fileList?.length) {
      return;
    }

    const imageFilesDT = new DataTransfer();
    const docFilesDT = new DataTransfer();
    for (const f of Array.from(fileList)) {
      if (f.type.startsWith("image/")) {
        imageFilesDT.items.add(f);
      } else {
        docFilesDT.items.add(f);
      }
    }

    const allRejected: string[] = [];
    const collectRejected = (names: string[]) => allRejected.push(...names);

    if (imageFilesDT.files.length > 0) {
      const urls = await readFilesAsDataUrls(imageFilesDT.files, collectRejected);
      if (urls.length) {
        setPendingImageUrls((prev) => [...prev, ...urls]);
      }
    }
    if (docFilesDT.files.length > 0) {
      const files = await readFilesAsBase64(docFilesDT.files, collectRejected);
      if (files.length) {
        setPendingFiles((prev) => [...prev, ...files]);
      }
    }

    if (allRejected.length) {
      showFileTooLarge(allRejected);
    }

    textareaRef.current?.focus();
  }, [showFileTooLarge]);

  useEffect(() => {
    const prevent = (e: DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);

  // ── Prevent browser scroll-on-focus from shifting the page ─────────────
  const rootRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const main = mainRef.current;

    const resetAllScroll = () => {
      if (root) {
        root.scrollTop = 0;
      }
      if (main) {
        main.scrollTop = 0;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    resetAllScroll();

    if (isConversationActive) {
      const onScroll = () => resetAllScroll();
      root?.addEventListener("scroll", onScroll);
      main?.addEventListener("scroll", onScroll);

      const raf1 = requestAnimationFrame(resetAllScroll);
      const raf2 = requestAnimationFrame(() =>
        requestAnimationFrame(resetAllScroll),
      );

      return () => {
        root?.removeEventListener("scroll", onScroll);
        main?.removeEventListener("scroll", onScroll);
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
  }, [isConversationActive]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        ref={rootRef}
        className={`ideaweave-entry${isDark ? " dark" : ""}${
          isChinese ? " ideaweave-entry--zh" : ""
        }${isConversationActive ? " ideaweave-entry--conversation" : ""}${
          appMode === "weave" ? " ideaweave-entry--weave" : ""
        }`}
      >
        {/* Background decorations */}
        <div className="ideaweave-entry__bg">
          <div className="ideaweave-entry__blob ideaweave-entry__blob--1" />
          <div className="ideaweave-entry__blob ideaweave-entry__blob--2" />
          <div className="ideaweave-entry__blob ideaweave-entry__blob--3" />
        </div>

        {/* Navigation — always visible */}
        <nav className="ideaweave-entry__nav">
          <div className="ideaweave-entry__logo">
            <span className="ideaweave-entry__logo-icon material-symbols-outlined">
              lightbulb
            </span>
            <span className="ideaweave-entry__logo-text">IdeaWeave</span>
          </div>
          <div className="ideaweave-entry__user">
            <div
              className="ideaweave-entry__avatar"
              role="button"
              tabIndex={0}
              onClick={onUserClick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  onUserClick?.();
                }
              }}
              aria-label={t("entry.userLogin")}
            >
              {isAuthLoading || avatarRetrying ? (
                <span className="ideaweave-entry__avatar-loading" />
              ) : userAvatarUrl && !avatarImgFailed ? (
                <img
                  key={`avatar-${avatarRetriesRef.current}`}
                  className="ideaweave-entry__avatar-img"
                  src={userAvatarUrl}
                  alt={userName}
                  onError={handleAvatarError}
                />
              ) : userInitials ? (
                <span className="ideaweave-entry__avatar-initials">
                  {userInitials}
                </span>
              ) : (
                <span className="material-symbols-outlined">person</span>
              )}
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main ref={mainRef} className="ideaweave-entry__main">
          {/* Header — hidden during conversation */}
          <div className="ideaweave-entry__header">
            <div className="ideaweave-entry__title-row">
              <h1 className="ideaweave-entry__title">Hi, {userName}.</h1>
            </div>
            <div className="ideaweave-entry__mode-row">
              <ModeToggle
                mode={appMode}
                onChange={handleModeChange}
                drawLabel={t("entry.modeDrawLabel")}
                drawDesc={t("entry.modeDrawDesc")}
                weaveLabel={t("entry.modeWeaveLabel")}
                weaveDesc={t("entry.modeWeaveDesc")}
                isDark={isDark}
              />
            </div>
          </div>

          {/* Card */}
          <div className="ideaweave-entry__card-wrapper">
            {/* Conversation back button — outside card, left of card-inner top */}
            {isConversationActive && (
              <button
                className="ideaweave-entry__conv-back"
                onClick={onBackFromConversation}
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            )}

            <div className="ideaweave-entry__card-shadow ideaweave-entry__card-shadow--1" />
            <div className="ideaweave-entry__card-shadow ideaweave-entry__card-shadow--2" />
            <div
              className={`ideaweave-entry__card${
                cardPulse ? " ideaweave-entry__card--pulse" : ""
              }`}
              onAnimationEnd={() => setCardPulse(false)}
            >
              <div className="ideaweave-entry__tape" />

              <div className="ideaweave-entry__card-inner">
                <div className="ideaweave-entry__texture" />

                {/* Entry content (hidden during conversation) */}
                <div
                  className={`ideaweave-entry__entry-content${
                    isConversationActive
                      ? " ideaweave-entry__entry-content--hidden"
                      : ""
                  }`}
                >
                  <div className="ideaweave-entry__input-header">
                    <h2>
                      {appMode === "draw"
                        ? t("entry.drawTitle")
                        : t("entry.sketchIdea")}
                    </h2>
                  </div>

                  <div
                    className={`ideaweave-entry__input-wrapper${
                      isDragOver
                        ? " ideaweave-entry__input-wrapper--dragover"
                        : ""
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <AttachImageThumbnails
                      urls={pendingImageUrls}
                      onRemove={(i) =>
                        setPendingImageUrls((prev) =>
                          prev.filter((_, j) => j !== i),
                        )
                      }
                      className="ideaweave-entry__thumbnails"
                    />
                    <AttachFileThumbnails
                      files={pendingFiles}
                      onRemove={(i) =>
                        setPendingFiles((prev) =>
                          prev.filter((_, j) => j !== i),
                        )
                      }
                      className="ideaweave-entry__file-list"
                    />
                    <textarea
                      ref={textareaRef}
                      className="ideaweave-entry__textarea"
                      placeholder={
                        appMode === "draw"
                          ? t("entry.drawPlaceholder")
                          : t("entry.placeholder")
                      }
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setIsTextareaFocused(true)}
                      onBlur={() => setIsTextareaFocused(false)}
                      onCompositionStart={() => setCompositionText("")}
                      onCompositionUpdate={(e) =>
                        setCompositionText(e.data ?? "")
                      }
                      onCompositionEnd={handleCompositionEnd}
                      rows={3}
                    />
                    {showMicHint && (
                      <p className="ideaweave-entry__mic-hint">
                        {appMode === "draw"
                          ? t("entry.drawMicHint")
                          : t("entry.micHint")}
                      </p>
                    )}
                    <div className="ideaweave-entry__input-actions">
                      <VoiceInputButton
                        onTranscript={handleVoiceTranscript}
                        onRecordingChange={setIsVoiceRecording}
                        variant="default"
                        layout="default"
                        className="ideaweave-entry__voice-btn"
                      />
                      <div className="ideaweave-entry__input-actions-right">
                        <AttachButton
                          onAddImages={(urls) => {
                            setPendingImageUrls((prev) => [...prev, ...urls]);
                            textareaRef.current?.focus();
                          }}
                          onAddFiles={(files) => {
                            setPendingFiles((prev) => [...prev, ...files]);
                            textareaRef.current?.focus();
                          }}
                          onFileTooLarge={showFileTooLarge}
                          buttonClassName="ideaweave-entry__image-btn"
                          title={t("conversation.addAttachment") ?? "Add image / file"}
                        />
                        <button
                          className="ideaweave-entry__weave-btn"
                          onClick={handleSubmit}
                          disabled={!hasContent}
                        >
                          <span>
                            {appMode === "draw"
                              ? t("entry.drawButton")
                              : t("entry.weaveIdea")}
                          </span>
                          <span className="material-symbols-outlined">
                            arrow_forward
                          </span>
                          <div className="ideaweave-entry__btn-shine" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conversation content (shown during conversation) */}
                {isConversationActive && conversationTopic && (
                  <ConversationContent
                    topic={conversationTopic}
                    initialImageUrls={conversationInitialImageUrls}
                    initialFiles={conversationInitialFiles}
                    userId={userId}
                    onBack={onBackFromConversation!}
                    onGoToDraw={onGoToDraw!}
                    onQuotaExceeded={handleConversationQuotaExceeded}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Sign-in prompt */}
          {!isAuthenticated && (
            <div className="ideaweave-entry__signin-prompt">
              <div className="ideaweave-entry__signin-prompt-inner">
                <div className="ideaweave-entry__signin-prompt-main">
                  <span className="material-symbols-outlined ideaweave-entry__signin-prompt-icon">
                    cloud_sync
                  </span>
                  <p className="ideaweave-entry__signin-prompt-text">
                    {t("entry.signInPrompt")}
                  </p>
                </div>
                <div className="ideaweave-entry__signin-prompt-warning">
                  <span className="material-symbols-outlined ideaweave-entry__signin-prompt-warning-icon">
                    warning
                  </span>
                  <span>{t("entry.signInWarning")}</span>
                </div>
                <button
                  type="button"
                  className="ideaweave-entry__signin-prompt-btn"
                  onClick={onUserClick}
                >
                  {t("entry.signUp")}
                </button>
              </div>
            </div>
          )}

          {/* Recent Projects */}
          {isAuthenticated && (recentDiagrams.length > 0 || isLoadingRecent) && (
            <div className="ideaweave-entry__recent">
              <h3 className="ideaweave-entry__recent-title">
                {t("entry.recentProjects")}
              </h3>
              <div className="ideaweave-entry__recent-grid">
                {isLoadingRecent
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="ideaweave-entry__recent-card ideaweave-entry__recent-card--skeleton"
                        aria-hidden
                      />
                    ))
                  : recentDiagrams.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className="ideaweave-entry__recent-card"
                        onClick={() => onSelectProject?.(d.id)}
                        aria-label={t("entry.openProject", { name: d.name })}
                      >
                        <div className="ideaweave-entry__recent-thumb">
                          {d.thumbnail ? (
                            <img
                              src={d.thumbnail}
                              alt=""
                              className="ideaweave-entry__recent-thumb-img"
                            />
                          ) : (
                            <span className="material-symbols-outlined ideaweave-entry__recent-thumb-icon">
                              dashboard_customize
                            </span>
                          )}
                        </div>
                        <span className="ideaweave-entry__recent-name">
                          {d.name}
                        </span>
                      </button>
                    ))}
              </div>
            </div>
          )}
        </main>

        {/* Footer — always visible */}
        <footer className="ideaweave-entry__footer">
          <p>
            {t("entry.footer")}
            <span className="ideaweave-entry__footer-links">
              <a href="/privacy">{t("entry.privacy")}</a>
              <span className="ideaweave-entry__footer-sep">·</span>
              <a href="/terms">{t("entry.terms")}</a>
            </span>
          </p>
        </footer>
      </div>

      {/* Quota limit modal — same UI as the canvas sidebar quota modal */}
      {showQuotaModal &&
        createPortal(
          <div className="iw-quota-modal" role="presentation">
            <div
              className="iw-quota-modal__backdrop"
              onClick={() => setShowQuotaModal(false)}
            />
            <div
              className="iw-quota-modal__panel"
              role="dialog"
              aria-modal="true"
              aria-label="Quota limit"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="iw-quota-modal__tape" aria-hidden="true" />

              <button
                type="button"
                className="iw-quota-modal__close"
                onClick={() => setShowQuotaModal(false)}
                aria-label="Close"
              >
                <span className="material-symbols-outlined">close</span>
              </button>

              <div className="iw-quota-modal__body">
                <span
                  className="material-symbols-outlined iw-quota-modal__icon"
                  aria-hidden="true"
                >
                  hourglass_top
                </span>

                <h2 className="iw-quota-modal__title">
                  {t("aiWeaver.quotaModalTitle")}
                </h2>

                <p className="iw-quota-modal__desc">
                  {quotaIsAnonymous
                    ? t("aiWeaver.quotaModalAnonDesc")
                    : t("aiWeaver.quotaModalUserDesc")}
                </p>

                {quotaIsAnonymous ? (
                  <button
                    type="button"
                    className="iw-quota-modal__cta"
                    onClick={() => {
                      setShowQuotaModal(false);
                      onOpenAuthModal?.();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      person_add
                    </span>
                    {t("aiWeaver.quotaModalSignUp")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="iw-quota-modal__ok"
                    onClick={() => setShowQuotaModal(false)}
                  >
                    {t("aiWeaver.quotaModalOk")}
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
      {toastMessage && (
        <UploadToast message={toastMessage} onClose={dismissToast} />
      )}
    </>
  );
};
